<template>
  <v-list class="py-0" density="comfortable">
    <v-list-item class="py-2" :prepend-gap="16">
      <template #prepend>
        <v-avatar size="40">
          <v-img :src="userAvatar" :alt="userFullName" />
        </v-avatar>
      </template>
      <v-list-item-title>{{ userFullName }}</v-list-item-title>
      <v-list-item-subtitle>{{ companyName }}</v-list-item-subtitle>
      <template v-if="mdAndDown" #append>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Close"
          data-test="profile-menu-close"
          @click="emit('close')"
        />
      </template>
    </v-list-item>
    <!-- BETA-CLEANUP -->
    <template v-if="isBetaAvailable">
      <v-divider />
      <ProfileMenuBetaPromo />
    </template>
    <!-- the compact top bar puts its notifications row here -->
    <slot />
    <v-divider />
    <v-list-item :to="getAccountRoute({ name: 'user-profile' })" @click="emit('close')">
      <template #prepend>
        <v-icon>mdi-account-circle</v-icon>
      </template>
      <v-list-item-title>Profile</v-list-item-title>
    </v-list-item>
    <v-list-item
      v-if="!isClient"
      :to="getAccountRoute({ name: 'administration-root-page' })"
      @click="emit('close')"
    >
      <template #prepend>
        <v-icon>mdi-cog</v-icon>
      </template>
      <v-list-item-title>Account Settings</v-list-item-title>
    </v-list-item>
    <v-list-item
      v-if="canSwitchAccount && activeAccount"
      to="/my-accounts"
      data-test="profile-menu-switch-account"
      @click="emit('close')"
    >
      <template #prepend>
        <v-icon>mdi-swap-horizontal</v-icon>
      </template>
      <v-list-item-title>Switch account</v-list-item-title>
      <v-list-item-subtitle>{{ activeAccount.name }}</v-list-item-subtitle>
    </v-list-item>
    <v-divider />
    <ThemeModeToggle />
    <!-- BETA-CLEANUP: the feedback and switch-back rows -->
    <template v-if="isBetaApp">
      <v-divider />
      <v-list-subheader>New version</v-list-subheader>
      <v-list-item data-test="beta-feedback" @click="openBetaFeedback">
        <template #prepend>
          <v-icon>mdi-message-alert-outline</v-icon>
        </template>
        <v-list-item-title>Report a problem with the new version</v-list-item-title>
      </v-list-item>
      <v-list-item
        data-test="leave-beta"
        data-track="beta.current-app.open"
        :disabled="isLeavingBeta"
        @click="switchBack"
      >
        <template #prepend>
          <v-progress-circular
            v-if="isLeavingBeta"
            indeterminate
            size="24"
            width="2"
            data-test="leave-beta-loader"
          />
          <v-icon v-else>mdi-swap-horizontal</v-icon>
        </template>
        <v-list-item-title>Switch back to the old version</v-list-item-title>
      </v-list-item>
    </template>
    <v-divider />
    <v-list-item data-test="profile-menu-logout" @click="logout">
      <template #prepend>
        <v-icon>mdi-logout</v-icon>
      </template>
      <v-list-item-title>Logout</v-list-item-title>
    </v-list-item>
  </v-list>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { useStore } from 'vuex';
import { useDisplay } from 'vuetify';

import { IUserAccount } from '@/bundles/User/interfaces';
import { RootActions } from '@/store/types/action-types';
import { useAccountRoute } from '@/bundles/App/composables/useAccountRoute';
import { useEventBus } from '@/bundles/App/composables/useEventBus';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { usePlaceholders } from '@/bundles/App/composables/usePlaceholders';
import { useUserRoles } from '@/bundles/App/composables/useUserRoles';
import { isBetaApp, isBetaAvailable, leaveBeta } from '@/bundles/App/helpers/betaChannel'; // BETA-CLEANUP

import ThemeModeToggle from '@/bundles/Panels/components/ThemeModeToggle.vue';
import ProfileMenuBetaPromo from '@/bundles/Panels/components/ProfileMenuBetaPromo.vue'; // BETA-CLEANUP

interface IEmits {
  close: [];
}

const emit = defineEmits<IEmits>();

const store = useStore();
const { mdAndDown } = useDisplay();
const { getAccountRoute } = useAccountRoute();
const { emit: emitEvent } = useEventBus();
const { addNotification } = useNotifications();
const { contactPlaceholder } = usePlaceholders();
const { isClient } = useUserRoles();

const userFullName = computed<string>(() => store.state.userName);
const companyName = computed<string>(() => store.state.company);
const userAvatar = computed<string>(() => store.state.user?.profile?.img || contactPlaceholder.value);
const userAccounts = computed<IUserAccount[]>(() => store.state.user?.account ?? []);
const activeAccount = computed<IUserAccount | undefined>(() => store.state.user?.active_account);

const canSwitchAccount = computed<boolean>(() => !!activeAccount.value && userAccounts.value.length > 1);

// BETA-CLEANUP: the existing request form, opened on a bug report that names the beta and the page it came from
const openBetaFeedback = async (): Promise<void> => {
  emit('close');
  emitEvent('open-request-form');
  await nextTick();
  emitEvent('open-bug-report');
  emitEvent('fill-bug-report', {
    name: '[New version] ',
    message: '',
    url: window.location.href,
  });
};

// BETA-CLEANUP: busy while leaveBeta saves the choice; it resolves as the page starts navigating away
const isLeavingBeta = ref(false);

const switchBack = async (): Promise<void> => {
  if (isLeavingBeta.value) return;

  isLeavingBeta.value = true;

  try {
    await leaveBeta();
  } catch (error: any) {
    addNotification({ ...error, status: 0 });
  } finally {
    isLeavingBeta.value = false;
  }
};

const logout = async (): Promise<void> => {
  try {
    await store.dispatch(RootActions.LOG_OUT);
  } catch (error: any) {
    addNotification({ ...error });
  }
};
</script>
